import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Button,
  ComboButton,
  Avatar,
  AvatarGroup,
  Popover,
  TextInput,
  PhoneInput,
  Textarea,
  Toggle,
  Modal,
  Accordion,
  Stepper,
  AlertModal,
  Banner,
  DatePicker,
  TimePicker,
  DateTimePicker,
  Tabs,
  LineLoader,
} from '@components/ui';
import Select from '@workpay/ds-shared/components/ui/Select';
import MultiSelect from '@workpay/ds-shared/components/ui/MultiSelect';
import type {
  ButtonVariant,
  ButtonSize,
  AvatarSize,
  AlertVariant,
  InputSize,
  BannerVariant,
} from '@components/ui';
import FullPageStepper from '@components/shared/FullPageStepper/FullPageStepper';
import type { FullPageStepperStep } from '@components/shared/FullPageStepper/FullPageStepper';
import Breadcrumbs from '@components/shared/Breadcrumbs/Breadcrumbs';
import useMultiStepForm from '@hooks/useMultiStepForm';
import flowerVase from '@assets/images/flower-vase.svg';
import s from './component-showcase.module.scss';

export const Route = createFileRoute('/component-showcase')({
  staticData: { hideShell: true },
  component: ComponentShowcase,
});

// ============================================
// ICONS (inline SVGs for demo)
// ============================================

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// ============================================
// SHOWCASE PAGE
// ============================================

function ComponentShowcase() {
  return (
    <div className={s.page}>
      {/* --- Header --- */}
      <header className={s.header}>
        <h1 className={s.title}>Component Showcase</h1>
        <p className={s.description}>
          Visual reference for all UI components with every size, variant, and state.
        </p>
      </header>

      {/* ============================== */}
      {/* BUTTONS */}
      {/* ============================== */}
      <ButtonShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* AVATARS */}
      {/* ============================== */}
      <AvatarShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* TEXT INPUTS */}
      {/* ============================== */}
      <TextInputShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* TEXTAREA */}
      {/* ============================== */}
      <TextareaShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* PHONE INPUT */}
      {/* ============================== */}
      <PhoneInputShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* TOGGLE */}
      {/* ============================== */}
      <ToggleShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* SELECT */}
      {/* ============================== */}
      <SelectShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* MULTI SELECT */}
      {/* ============================== */}
      <MultiSelectShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* MODAL */}
      {/* ============================== */}
      <ModalShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* ALERT MODAL */}
      {/* ============================== */}
      <AlertModalShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* ACCORDION */}
      {/* ============================== */}
      <AccordionShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* STEPPER */}
      {/* ============================== */}
      <StepperShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* BANNER */}
      {/* ============================== */}
      <BannerShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* POPOVER */}
      {/* ============================== */}
      <PopoverShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* FULL PAGE STEPPER */}
      {/* ============================== */}
      <FullPageStepperShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* BREADCRUMBS */}
      {/* ============================== */}
      <BreadcrumbsShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* DATE / TIME PICKERS */}
      {/* ============================== */}
      <DateTimeShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* TABS */}
      {/* ============================== */}
      <TabsShowcase />

      <div className={s.divider} />

      {/* ============================== */}
      {/* LINE LOADER */}
      {/* ============================== */}
      <LineLoaderShowcase />
    </div>
  );
}

// ============================================
// BUTTON SHOWCASE
// ============================================

const BTN_VARIANTS: ButtonVariant[] = ['filled', 'outline', 'ghost', 'link', 'danger'];
const BTN_SIZES: ButtonSize[] = ['sm', 'md', 'lg', 'xl'];

function ButtonShowcase() {
  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Buttons</h2>

      {/* --- Variants × Sizes --- */}
      {BTN_VARIANTS.map((variant) => (
        <div key={variant} className={s.subsection}>
          <h3 className={s.subsectionTitle}>
            {variant.charAt(0).toUpperCase() + variant.slice(1)}
          </h3>

          {/* Text only */}
          <div className={s.row}>
            <span className={s.label}>Text only</span>
            {BTN_SIZES.map((size) => (
              <Button key={size} variant={variant} size={size}>
                Button
              </Button>
            ))}
          </div>

          {/* Left icon + text */}
          <div className={s.row}>
            <span className={s.label}>Left icon</span>
            {BTN_SIZES.map((size) => (
              <Button key={size} variant={variant} size={size} leftIcon={<PlusIcon />}>
                Button
              </Button>
            ))}
          </div>

          {/* Text + right icon */}
          <div className={s.row}>
            <span className={s.label}>Right icon</span>
            {BTN_SIZES.map((size) => (
              <Button key={size} variant={variant} size={size} rightIcon={<ChevronIcon />}>
                Button
              </Button>
            ))}
          </div>

          {/* Icon only */}
          {variant !== 'link' && (
            <div className={s.row}>
              <span className={s.label}>Icon only</span>
              {BTN_SIZES.map((size) => (
                <Button key={size} variant={variant} size={size} iconOnly>
                  <PlusIcon />
                </Button>
              ))}
            </div>
          )}

          {/* Disabled */}
          <div className={s.row}>
            <span className={s.label}>Disabled</span>
            {BTN_SIZES.map((size) => (
              <Button key={size} variant={variant} size={size} disabled>
                Button
              </Button>
            ))}
          </div>

          {/* Loading */}
          <div className={s.row}>
            <span className={s.label}>Loading</span>
            {BTN_SIZES.map((size) => (
              <Button key={size} variant={variant} size={size} loading>
                Button
              </Button>
            ))}
          </div>
        </div>
      ))}

      {/* --- Combo Button --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Combo Button</h3>

        <div className={s.row}>
          <span className={s.label}>Filled</span>
          {BTN_SIZES.map((size) => (
            <ComboButton key={size} mainProps={{ children: 'Button' }} size={size} variant="filled" />
          ))}
        </div>

        <div className={s.row}>
          <span className={s.label}>Danger</span>
          {BTN_SIZES.map((size) => (
            <ComboButton key={size} mainProps={{ children: 'Button' }} size={size} variant="danger" />
          ))}
        </div>

        <div className={s.row}>
          <span className={s.label}>Disabled</span>
          <ComboButton mainProps={{ children: 'Button' }} size="lg" variant="filled" disabled />
          <ComboButton mainProps={{ children: 'Button' }} size="lg" variant="danger" disabled />
        </div>
      </div>

      {/* --- Full Width --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Full Width</h3>
        <Button variant="filled" size="lg" fullWidth>
          Full Width Button
        </Button>
      </div>
    </section>
  );
}

// ============================================
// AVATAR SHOWCASE
// ============================================

const AVATAR_SIZES: AvatarSize[] = ['xs', 'sm', 'md', 'lg'];

const SAMPLE_PEOPLE = [
  { name: 'Bruce Wayne', initials: 'BW' },
  { name: 'Robin Blake', initials: 'RB' },
  { name: 'Thomas Mercer', initials: 'TM' },
  { name: 'Diana Kyle', initials: 'DK' },
  { name: 'Oliver Lance', initials: 'OL' },
  { name: 'Selina Gordon', initials: 'SG' },
];

const GROUP_AVATARS = [
  { name: 'Bruce Wayne' },
  { name: 'Natasha Torres' },
  { name: 'Amara Nwosu' },
  { name: 'Clark Kent' },
  { name: 'Diana Prince' },
  { name: 'Barry Allen' },
  { name: 'Arthur Curry' },
  { name: 'Victor Stone' },
  { name: 'Hal Jordan' },
  { name: 'Oliver Queen' },
  { name: 'Lois Parker' },
];

function AvatarShowcase() {
  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Avatars</h2>

      {/* --- Sizes with Initials --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Sizes — Initials</h3>
        <div className={s.row}>
          {AVATAR_SIZES.map((size) => (
            <div key={size} className={s.column}>
              <span className={s.labelInline}>{size}</span>
              <Avatar name="Bruce Wayne" size={size} />
            </div>
          ))}
        </div>
      </div>

      {/* --- Color Palette --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Initials Color Palette</h3>
        {(['xs', 'sm', 'md', 'lg'] as AvatarSize[]).map((size) => (
          <div key={size} className={s.row}>
            <span className={s.label}>{size}</span>
            {SAMPLE_PEOPLE.map((p) => (
              <Avatar key={p.name} name={p.name} size={size} />
            ))}
          </div>
        ))}
      </div>

      {/* --- With Label --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>With Label</h3>
        <div className={s.row}>
          <span className={s.label}>sm</span>
          <Avatar name="Bruce Wayne" size="sm" label="Bruce Wayne" />
        </div>
        <div className={s.row}>
          <span className={s.label}>md</span>
          <Avatar name="Bruce Wayne" size="md" label="Bruce Wayne" />
        </div>
      </div>

      {/* --- With Label + Subtitle --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>With Label + Subtitle</h3>
        <div className={s.row}>
          <span className={s.label}>sm</span>
          <Avatar
            name="Bruce Wayne"
            size="sm"
            label="Bruce Wayne"
            subtitle="1007 Mountain Drive, Gotham"
          />
        </div>
        <div className={s.row}>
          <span className={s.label}>md</span>
          <Avatar
            name="Bruce Wayne"
            size="md"
            label="Bruce Wayne"
            subtitle="1007 Mountain Drive, Gotham"
          />
        </div>
      </div>

      {/* --- Avatar Group --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Avatar Group</h3>

        <div className={s.row}>
          <span className={s.label}>sm, max 4</span>
          <AvatarGroup size="sm" max={4} avatars={GROUP_AVATARS} />
        </div>
        <div className={s.row}>
          <span className={s.label}>md, max 4</span>
          <AvatarGroup size="md" max={4} avatars={GROUP_AVATARS} />
        </div>
        <div className={s.row}>
          <span className={s.label}>sm, brand</span>
          <AvatarGroup size="sm" max={4} avatars={GROUP_AVATARS} overflowStyle="brand" />
        </div>
        <div className={s.row}>
          <span className={s.label}>md, brand</span>
          <AvatarGroup size="md" max={4} avatars={GROUP_AVATARS} overflowStyle="brand" />
        </div>
      </div>
    </section>
  );
}

// ============================================
// TEXT INPUT SHOWCASE
// ============================================

const INPUT_SIZES: InputSize[] = ['sm', 'md', 'lg'];

function TextInputShowcase() {
  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Text Input</h2>

      {/* --- Sizes --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Sizes</h3>
        <div className={s.grid}>
          {INPUT_SIZES.map((size) => (
            <TextInput key={size} size={size} label={`Size: ${size}`} placeholder="Enter text" />
          ))}
        </div>
      </div>

      {/* --- States --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>States</h3>
        <div className={s.grid}>
          <TextInput label="Default" placeholder="Placeholder text" />
          <TextInput label="With value" defaultValue="Hello World" />
          <TextInput label="Required" placeholder="Required field" isRequired />
          <TextInput label="With helper" placeholder="Enter text" helperText="This is a hint" />
          <TextInput label="With error" placeholder="Enter text" error="This field is required" />
          <TextInput label="Disabled" placeholder="Disabled" disabled />
        </div>
      </div>

      {/* --- With Icons --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>With Icons</h3>
        <div className={s.grid}>
          <TextInput label="Left icon" placeholder="Search..." leftIcon={<SearchIcon />} />
          <TextInput label="Right icon" placeholder="Enter text" rightIcon={<ChevronIcon />} />
          <TextInput
            label="Both icons"
            placeholder="Search..."
            leftIcon={<SearchIcon />}
            rightIcon={<ChevronIcon />}
          />
        </div>
      </div>
    </section>
  );
}

// ============================================
// TEXTAREA SHOWCASE
// ============================================

function TextareaShowcase() {
  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Textarea</h2>

      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Variants</h3>
        <div className={s.grid}>
          <Textarea label="Default" placeholder="Enter description..." />
          <Textarea label="Required" placeholder="Required field" isRequired />
          <Textarea label="With helper" placeholder="Enter text" helperText="Max 500 characters" />
          <Textarea label="With error" placeholder="Enter text" error="Description is required" />
          <Textarea label="Disabled" placeholder="Disabled" disabled />
          <Textarea label="With value" defaultValue="Lorem ipsum dolor sit amet, consectetur adipiscing elit." />
        </div>
      </div>
    </section>
  );
}

// ============================================
// PHONE INPUT SHOWCASE
// ============================================

function PhoneInputShowcase() {
  const [phone, setPhone] = useState('');

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Phone Input</h2>

      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Variants</h3>
        <div className={s.grid}>
          <PhoneInput label="Default" placeholder="712 345 678" value={phone} onChange={setPhone} />
          <PhoneInput label="Required" placeholder="712 345 678" isRequired />
          <PhoneInput label="With helper" placeholder="712 345 678" helperText="Include area code" />
          <PhoneInput label="With error" placeholder="712 345 678" error="Invalid phone number" />
          <PhoneInput label="Disabled" placeholder="712 345 678" disabled />
        </div>
      </div>
    </section>
  );
}

// ============================================
// TOGGLE SHOWCASE
// ============================================

function ToggleShowcase() {
  const [toggle1, setToggle1] = useState(false);
  const [toggle2, setToggle2] = useState(true);

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Toggle</h2>

      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>States</h3>
        <div className={s.row}>
          <span className={s.label}>Off</span>
          <Toggle isChecked={toggle1} onChange={setToggle1} />
        </div>
        <div className={s.row}>
          <span className={s.label}>On</span>
          <Toggle isChecked={toggle2} onChange={setToggle2} />
        </div>
        <div className={s.row}>
          <span className={s.label}>Disabled off</span>
          <Toggle isChecked={false} disabled />
        </div>
        <div className={s.row}>
          <span className={s.label}>Disabled on</span>
          <Toggle isChecked disabled />
        </div>
      </div>

      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>With Label</h3>
        <div className={s.row}>
          <Toggle isChecked={toggle1} onChange={setToggle1} label="Label right (default)" />
        </div>
        <div className={s.row}>
          <Toggle isChecked={toggle2} onChange={setToggle2} label="Label left" labelPosition="left" />
        </div>
      </div>
    </section>
  );
}

// ============================================
// SELECT SHOWCASE
// ============================================

const SAMPLE_OPTIONS = [
  { label: 'Kenya', value: 'ke' },
  { label: 'Uganda', value: 'ug' },
  { label: 'Tanzania', value: 'tz' },
  { label: 'Rwanda', value: 'rw' },
  { label: 'Ethiopia', value: 'et' },
  { label: 'Nigeria', value: 'ng', disabled: true },
];

function SelectShowcase() {
  const [val1, setVal1] = useState('');
  const [val2, setVal2] = useState('ke');
  const [val3, setVal3] = useState('');

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Select</h2>

      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Variants</h3>
        <div className={s.grid}>
          <Select
            label="Default"
            options={SAMPLE_OPTIONS}
            value={val1}
            onChange={setVal1}
            placeholder="Select country"
          />
          <Select
            label="With value"
            options={SAMPLE_OPTIONS}
            value={val2}
            onChange={setVal2}
          />
          <Select
            label="Searchable"
            options={SAMPLE_OPTIONS}
            value={val3}
            onChange={setVal3}
            searchable
            placeholder="Search countries..."
          />
          <Select
            label="Required"
            options={SAMPLE_OPTIONS}
            value=""
            onChange={() => {}}
            isRequired
          />
          <Select
            label="With error"
            options={SAMPLE_OPTIONS}
            value=""
            onChange={() => {}}
            error="Country is required"
          />
          <Select
            label="Disabled"
            options={SAMPLE_OPTIONS}
            value="ke"
            onChange={() => {}}
            disabled
          />
          <Select
            label="Loading"
            options={[]}
            value=""
            onChange={() => {}}
            loading
          />
        </div>
      </div>
    </section>
  );
}

// ============================================
// MULTI SELECT SHOWCASE
// ============================================

function MultiSelectShowcase() {
  const [vals1, setVals1] = useState<string[]>([]);
  const [vals2, setVals2] = useState<string[]>(['ke', 'ug', 'tz', 'rw']);

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Multi Select</h2>

      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Variants</h3>
        <div className={s.grid}>
          <MultiSelect
            label="Default"
            options={SAMPLE_OPTIONS}
            value={vals1}
            onChange={setVals1}
            placeholder="Select countries"
          />
          <MultiSelect
            label="With selections (max 3 chips)"
            options={SAMPLE_OPTIONS}
            value={vals2}
            onChange={setVals2}
            maxVisibleChips={3}
          />
          <MultiSelect
            label="Searchable"
            options={SAMPLE_OPTIONS}
            value={vals1}
            onChange={setVals1}
            searchable
            placeholder="Search countries..."
          />
          <MultiSelect
            label="Required"
            options={SAMPLE_OPTIONS}
            value={[]}
            onChange={() => {}}
            isRequired
          />
          <MultiSelect
            label="With error"
            options={SAMPLE_OPTIONS}
            value={[]}
            onChange={() => {}}
            error="Select at least one country"
          />
          <MultiSelect
            label="Disabled"
            options={SAMPLE_OPTIONS}
            value={['ke']}
            onChange={() => {}}
            disabled
          />
        </div>
      </div>
    </section>
  );
}

// ============================================
// MODAL SHOWCASE
// ============================================

function ModalShowcase() {
  const [smOpen, setSmOpen] = useState(false);
  const [mdOpen, setMdOpen] = useState(false);
  const [lgOpen, setLgOpen] = useState(false);
  const [footerOpen, setFooterOpen] = useState(false);

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Modal</h2>

      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Sizes</h3>
        <div className={s.row}>
          <Button variant="outline" size="md" onClick={() => setSmOpen(true)}>
            Small Modal
          </Button>
          <Button variant="outline" size="md" onClick={() => setMdOpen(true)}>
            Medium Modal
          </Button>
          <Button variant="outline" size="md" onClick={() => setLgOpen(true)}>
            Large Modal
          </Button>
          <Button variant="filled" size="md" onClick={() => setFooterOpen(true)}>
            With Footer
          </Button>
        </div>
      </div>

      <Modal isOpen={smOpen} onClose={() => setSmOpen(false)} title="Small Modal" size="sm">
        <p>This is a small modal (480px). Good for confirmations and simple forms.</p>
      </Modal>

      <Modal isOpen={mdOpen} onClose={() => setMdOpen(false)} title="Medium Modal" size="md">
        <p>This is a medium modal (640px). The default size, suitable for most use cases.</p>
      </Modal>

      <Modal isOpen={lgOpen} onClose={() => setLgOpen(false)} title="Large Modal" size="lg">
        <p>This is a large modal (900px). Good for complex forms, previews, and multi-step flows.</p>
      </Modal>

      <Modal
        isOpen={footerOpen}
        onClose={() => setFooterOpen(false)}
        title="Modal with Footer"
        size="md"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button variant="outline" size="md" onClick={() => setFooterOpen(false)}>
              Cancel
            </Button>
            <Button variant="filled" size="md" onClick={() => setFooterOpen(false)}>
              Save Changes
            </Button>
          </div>
        }
      >
        <p>This modal has a sticky footer with action buttons.</p>
      </Modal>
    </section>
  );
}

// ============================================
// ALERT MODAL SHOWCASE
// ============================================

const ALERT_VARIANTS: AlertVariant[] = ['success', 'danger', 'warning', 'error'];

const ALERT_DEMOS: Record<AlertVariant, { title: string; description: string; primary: string; secondary: string }> = {
  success: {
    title: 'You have successfully performed an action',
    description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse cursus fermentum risus, sit amet fringilla nunc pellentesque quis.',
    primary: 'Continue',
    secondary: 'Cancel',
  },
  danger: {
    title: 'Delete employee?',
    description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse cursus fermentum risus, sit amet fringilla nunc pellentesque quis.',
    primary: 'Delete',
    secondary: 'Cancel',
  },
  warning: {
    title: 'Set employee on leave?',
    description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse cursus fermentum risus, sit amet fringilla nunc pellentesque quis.',
    primary: 'Confirm',
    secondary: 'Cancel',
  },
  error: {
    title: 'Transaction failed',
    description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse cursus fermentum risus, sit amet fringilla nunc pellentesque quis.',
    primary: 'Try again',
    secondary: 'Close',
  },
};

function AlertModalShowcase() {
  const [openAlert, setOpenAlert] = useState<AlertVariant | null>(null);

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Alert Modal</h2>

      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Variants</h3>
        <div className={s.row}>
          {ALERT_VARIANTS.map((variant) => (
            <Button
              key={variant}
              variant={variant === 'danger' || variant === 'error' ? 'danger' : variant === 'success' ? 'filled' : 'outline'}
              size="md"
              onClick={() => setOpenAlert(variant)}
            >
              {variant.charAt(0).toUpperCase() + variant.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {ALERT_VARIANTS.map((variant) => {
        const demo = ALERT_DEMOS[variant];
        return (
          <AlertModal
            key={variant}
            variant={variant}
            title={demo.title}
            description={demo.description}
            isOpen={openAlert === variant}
            onClose={() => setOpenAlert(null)}
            primaryLabel={demo.primary}
            onPrimaryClick={() => setOpenAlert(null)}
            secondaryLabel={demo.secondary}
          />
        );
      })}
    </section>
  );
}

// ============================================
// ACCORDION SHOWCASE
// ============================================

function AccordionShowcase() {
  const [controlled, setControlled] = useState(false);

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Accordion</h2>

      {/* --- Plain variant --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Plain (default)</h3>
        <Accordion title="Plain accordion — right chevron (default)">
          <p>This is the default accordion with a right-aligned chevron and transparent background.</p>
        </Accordion>
        <Accordion title="Plain — left chevron" chevronPosition="left">
          <p>Same plain variant but with the chevron on the left side.</p>
        </Accordion>
        <Accordion title="With subtitle" subtitle="Additional context about this section">
          <p>This accordion has a subtitle below the title.</p>
        </Accordion>
        <Accordion title="Default expanded" defaultExpanded>
          <p>This accordion starts in the expanded state.</p>
        </Accordion>
      </div>

      {/* --- Filled variant --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Filled</h3>
        <Accordion title="Filled accordion" variant="filled">
          <p>Filled variant with a tinted background header.</p>
        </Accordion>
        <Accordion title="Filled — left chevron" variant="filled" chevronPosition="left">
          <p>Filled variant with left-aligned chevron.</p>
        </Accordion>
        <Accordion title="Filled + white body" variant="filled" whiteBody defaultExpanded>
          <p>Filled variant with a white body panel — used for form cards in the template editor.</p>
        </Accordion>
      </div>

      {/* --- With icon & action --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>With Icon & Action</h3>
        <Accordion
          title="With icon"
          variant="filled"
          icon={<SearchIcon />}
          defaultExpanded
        >
          <p>Accordion with a custom icon in the header.</p>
        </Accordion>
        <Accordion
          title="With action slot"
          variant="filled"
          action={
            <Button variant="ghost" size="sm" iconOnly>
              <PlusIcon />
            </Button>
          }
        >
          <p>Accordion with an action button in the header (outside the toggle trigger).</p>
        </Accordion>
      </div>

      {/* --- Controlled --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Controlled</h3>
        <div className={s.row}>
          <Button variant="outline" size="sm" onClick={() => setControlled(!controlled)}>
            Toggle externally ({controlled ? 'open' : 'closed'})
          </Button>
        </div>
        <Accordion
          title="Controlled accordion"
          variant="filled"
          expanded={controlled}
          onExpandedChange={setControlled}
        >
          <p>This accordion&apos;s state is controlled by the button above.</p>
        </Accordion>
      </div>
    </section>
  );
}

// ============================================
// STEPPER SHOWCASE
// ============================================

const STEPPER_STEPS = [
  { id: 'details', label: 'Company Details' },
  { id: 'templates', label: 'Form Templates' },
  { id: 'review', label: 'Review' },
  { id: 'done', label: 'Complete' },
];

function StepperShowcase() {
  const [activeStep, setActiveStep] = useState(1);

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Stepper</h2>

      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Interactive</h3>
        <Stepper
          steps={STEPPER_STEPS}
          activeStep={activeStep}
          onStepClick={(i) => setActiveStep(i)}
        />
        <div className={s.row} style={{ marginTop: 24 }}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
            disabled={activeStep === 0}
          >
            Previous
          </Button>
          <Button
            variant="filled"
            size="sm"
            onClick={() => setActiveStep((s) => Math.min(STEPPER_STEPS.length - 1, s + 1))}
            disabled={activeStep === STEPPER_STEPS.length - 1}
          >
            Next
          </Button>
        </div>
      </div>

      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>States</h3>
        <div className={s.column}>
          <span className={s.labelInline}>Step 1 active (all upcoming)</span>
          <Stepper steps={STEPPER_STEPS} activeStep={0} />
        </div>
        <div className={s.column}>
          <span className={s.labelInline}>Step 2 active (1 completed)</span>
          <Stepper steps={STEPPER_STEPS} activeStep={1} />
        </div>
        <div className={s.column}>
          <span className={s.labelInline}>Step 3 active (2 completed)</span>
          <Stepper steps={STEPPER_STEPS} activeStep={2} />
        </div>
        <div className={s.column}>
          <span className={s.labelInline}>Last step active (3 completed)</span>
          <Stepper steps={STEPPER_STEPS} activeStep={3} />
        </div>
      </div>
    </section>
  );
}

// ============================================
// BANNER SHOWCASE
// ============================================

const BANNER_VARIANTS: BannerVariant[] = ['default', 'success', 'warning', 'critical', 'informational'];

function BannerShowcase() {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const isDismissed = (key: string) => dismissed.has(key);
  const dismiss = (key: string) => setDismissed((prev) => new Set(prev).add(key));
  const resetAll = () => setDismissed(new Set());

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Banner</h2>

      {/* --- All variants, title only --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Variants — Title Only</h3>
        <div className={s.column}>
          {BANNER_VARIANTS.map((variant) => (
            <Banner key={variant} title="Banner header information" variant={variant} />
          ))}
        </div>
      </div>

      {/* --- With description --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>With Description</h3>
        <div className={s.column}>
          {BANNER_VARIANTS.map((variant) => (
            <Banner
              key={variant}
              title="Banner header information"
              description="Here's a supporting text to support this banner information"
              variant={variant}
            />
          ))}
        </div>
      </div>

      {/* --- Dismissible --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Dismissible</h3>
        <div className={s.row}>
          <Button variant="outline" size="sm" onClick={resetAll}>
            Reset dismissed
          </Button>
        </div>
        <div className={s.column}>
          {BANNER_VARIANTS.map((variant) =>
            isDismissed(`dismiss-${variant}`) ? null : (
              <Banner
                key={variant}
                title="Banner header information"
                description="Here's a supporting text to support this banner information"
                variant={variant}
                dismissible
                onDismiss={() => dismiss(`dismiss-${variant}`)}
              />
            ),
          )}
        </div>
      </div>

      {/* --- With primary action --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>With Primary Action</h3>
        <div className={s.column}>
          {BANNER_VARIANTS.map((variant) => (
            <Banner
              key={variant}
              title="Banner header information"
              description="Here's a supporting text to support this banner information"
              variant={variant}
              primaryAction={<Button variant="outline" size="sm">Button</Button>}
            />
          ))}
        </div>
      </div>

      {/* --- With both actions --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>With Primary + Secondary Actions</h3>
        <div className={s.column}>
          {BANNER_VARIANTS.map((variant) => (
            <Banner
              key={variant}
              title="Banner header information"
              description="Here's a supporting text to support this banner information"
              variant={variant}
              primaryAction={<Button variant="outline" size="sm">Button</Button>}
              secondaryAction={<Button variant="ghost" size="sm">Button</Button>}
            />
          ))}
        </div>
      </div>

      {/* --- Full: actions + dismiss --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Full — Actions + Dismiss</h3>
        <div className={s.column}>
          {BANNER_VARIANTS.map((variant) =>
            isDismissed(`full-${variant}`) ? null : (
              <Banner
                key={variant}
                title="Banner header information"
                description="Here's a supporting text to support this banner information"
                variant={variant}
                dismissible
                onDismiss={() => dismiss(`full-${variant}`)}
                primaryAction={<Button variant="outline" size="sm">Button</Button>}
                secondaryAction={<Button variant="ghost" size="sm">Button</Button>}
              />
            ),
          )}
        </div>
      </div>

      {/* --- With image --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>With Image (replaces icon)</h3>
        <div className={s.column}>
          {BANNER_VARIANTS.map((variant) => (
            <Banner
              key={variant}
              title="Banner header information"
              description="Here's a supporting text to support this banner information"
              variant={variant}
              image={<img src={flowerVase} alt="" />}
              primaryAction={<Button variant="outline" size="sm">Button</Button>}
              secondaryAction={<Button variant="ghost" size="sm">Button</Button>}
            />
          ))}
        </div>
      </div>

      {/* --- Image + dismiss --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Image + Dismiss</h3>
        <div className={s.column}>
          {BANNER_VARIANTS.map((variant) =>
            isDismissed(`img-${variant}`) ? null : (
              <Banner
                key={variant}
                title="Banner header information"
                description="Here's a supporting text to support this banner information"
                variant={variant}
                image={<img src={flowerVase} alt="" />}
                dismissible
                onDismiss={() => dismiss(`img-${variant}`)}
                primaryAction={<Button variant="outline" size="sm">Button</Button>}
                secondaryAction={<Button variant="ghost" size="sm">Button</Button>}
              />
            ),
          )}
        </div>
      </div>
    </section>
  );
}

// ============================================
// POPOVER SHOWCASE
// ============================================

function PopoverShowcase() {
  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Popover</h2>

      {/* --- Hover --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Hover Trigger</h3>
        <div className={s.row}>
          <Popover content={<PopoverDemoContent />} placement="bottom-start">
            <Button variant="outline" size="md">
              Hover me
            </Button>
          </Popover>
          <Popover content={<PopoverDemoContent />} placement="bottom">
            <Button variant="ghost" size="md">
              Hover (center)
            </Button>
          </Popover>
          <Popover content={<PopoverDemoContent />} placement="right">
            <Button variant="outline" size="md">
              Hover (right)
            </Button>
          </Popover>
        </div>
      </div>

      {/* --- Click --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Click Trigger</h3>
        <div className={s.row}>
          <Popover
            content={<PopoverDemoContent />}
            trigger="click"
            placement="bottom-start"
          >
            <Button variant="filled" size="md">
              Click me
            </Button>
          </Popover>
          <Popover
            content={<PopoverDemoContent />}
            trigger="click"
            placement="bottom-end"
          >
            <Button variant="danger" size="md">
              Click (end)
            </Button>
          </Popover>
        </div>
      </div>

      {/* --- Placements --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Placements</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, padding: '60px 0' }}>
          {(
            [
              'top-start',
              'top',
              'top-end',
              'left',
              'right',
              'bottom-start',
              'bottom',
              'bottom-end',
            ] as const
          ).map((p) => (
            <Popover
              key={p}
              content={<span style={{ whiteSpace: 'nowrap' }}>{p}</span>}
              placement={p}
              trigger="click"
            >
              <Button variant="outline" size="sm">
                {p}
              </Button>
            </Popover>
          ))}
        </div>
      </div>

      {/* --- Avatar Hover Card --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Avatar Hover Card (future pattern)</h3>
        <div className={s.row}>
          <Popover
            content={
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Avatar
                  name="Bruce Wayne"
                  size="md"
                  label="Bruce Wayne"
                  subtitle="bruce@wayne.enterprises"
                />
                <div style={{ fontSize: 13, color: '#6C726C' }}>
                  CEO &middot; Wayne Enterprises
                </div>
              </div>
            }
            placement="bottom-start"
            openDelay={300}
          >
            <Avatar name="Bruce Wayne" size="sm" />
          </Popover>

          <Popover
            content={
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Avatar
                  name="Diana Prince"
                  size="md"
                  label="Diana Prince"
                  subtitle="diana@themyscira.gov"
                />
                <div style={{ fontSize: 13, color: '#6C726C' }}>
                  Ambassador &middot; Themyscira
                </div>
              </div>
            }
            placement="bottom-start"
            openDelay={300}
          >
            <Avatar name="Diana Prince" size="sm" />
          </Popover>

          <Popover
            content={
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Avatar
                  name="Clark Kent"
                  size="md"
                  label="Clark Kent"
                  subtitle="clark@dailyplanet.com"
                />
                <div style={{ fontSize: 13, color: '#6C726C' }}>
                  Reporter &middot; Daily Planet
                </div>
              </div>
            }
            placement="bottom-start"
            openDelay={300}
          >
            <Avatar name="Clark Kent" size="sm" />
          </Popover>
        </div>
      </div>
    </section>
  );
}

// --- Demo content for generic popovers ---
function PopoverDemoContent() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <strong style={{ fontSize: 14 }}>Popover Title</strong>
      <p style={{ fontSize: 13, color: '#6C726C', margin: 0 }}>
        This is a generic popover panel. It can hold any content.
      </p>
    </div>
  );
}

// ============================================
// FULL PAGE STEPPER SHOWCASE
// ============================================

const FULL_PAGE_STEPPER_STEPS: FullPageStepperStep[] = [
  {
    id: 'entity-details',
    label: 'Entity Details',
    title: 'Entity Details',
    description: 'Upload your company details to get started with entity setup.',
  },
  {
    id: 'documents',
    label: 'Documents',
    title: 'Upload Documents',
    description: 'Provide the required legal documents for verification.',
  },
  {
    id: 'bank-details',
    label: 'Bank Details',
    title: 'Bank Account Information',
    description: 'Add your primary bank account for payroll processing.',
  },
  {
    id: 'compliance',
    label: 'Compliance',
    title: 'Compliance Setup',
    description: 'Configure tax and regulatory compliance settings.',
  },
  {
    id: 'review',
    label: 'Review & Submit',
    title: 'Review & Submit',
    description: 'Review all information before final submission.',
  },
];

function FullPageStepperShowcase() {
  const [showStepper, setShowStepper] = useState(false);
  const stepper = useMultiStepForm({ totalSteps: FULL_PAGE_STEPPER_STEPS.length });

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Full Page Stepper</h2>

      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Interactive Demo</h3>
        <p style={{ fontSize: 14, color: '#6C726C', marginBottom: 16 }}>
          Opens a full-viewport stepper layout. Use Continue/Back to navigate, or click completed
          steps in the sidebar.
        </p>
        <div className={s.row}>
          <Button
            variant="filled"
            onClick={() => {
              stepper.reset();
              setShowStepper(true);
            }}
          >
            Open Full Page Stepper
          </Button>
        </div>
      </div>

      {showStepper && (
        <FullPageStepper
          pageTitle="Setup your Entity"
          steps={FULL_PAGE_STEPPER_STEPS}
          activeStep={stepper.currentStep}
          onClose={() => setShowStepper(false)}
          onStepClick={stepper.goToStep}
          headerAction={
            <Button variant="link" size="sm">
              Need Help?
            </Button>
          }
          footer={
            <>
              {!stepper.isFirstStep && (
                <Button variant="outline" onClick={stepper.goToPrev}>
                  Back
                </Button>
              )}
              <Button
                variant="filled"
                onClick={() => {
                  if (stepper.isLastStep) {
                    setShowStepper(false);
                  } else {
                    void stepper.goToNext();
                  }
                }}
              >
                {stepper.isLastStep ? 'Submit' : 'Continue'}
              </Button>
            </>
          }
        >
          <FullPageStepperDemoContent step={stepper.currentStep} />
        </FullPageStepper>
      )}
    </section>
  );
}

/** Demo content rendered inside each step of the FullPageStepper showcase. */
function FullPageStepperDemoContent({ step }: { step: number }) {
  const stepContent = [
    // Step 0 — Entity Details
    <div key="entity" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <TextInput label="Entity Name" placeholder="Acme Corporation" />
      <TextInput label="Registration Number" placeholder="REG-2024-001" />
      <TextInput label="Country" placeholder="United States" />
    </div>,
    // Step 1 — Documents
    <div key="docs" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          border: '2px dashed #d2d5d2',
          borderRadius: 8,
          padding: 40,
          textAlign: 'center',
          color: '#6C726C',
        }}
      >
        Drag and drop files here, or click to browse
      </div>
      <TextInput label="Document Reference" placeholder="DOC-001" />
    </div>,
    // Step 2 — Bank Details
    <div key="bank" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <TextInput label="Bank Name" placeholder="First National Bank" />
      <TextInput label="Account Number" placeholder="****1234" />
      <TextInput label="Routing Number" placeholder="021000021" />
    </div>,
    // Step 3 — Compliance
    <div key="compliance" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <TextInput label="Tax ID" placeholder="XX-XXXXXXX" />
      <TextInput label="Regulatory Body" placeholder="SEC" />
    </div>,
    // Step 4 — Review
    <div key="review" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          background: '#f0f8ec',
          borderRadius: 8,
          padding: 20,
          color: '#3b662a',
          fontSize: 14,
        }}
      >
        All steps completed. Review your information above and click Submit to finish.
      </div>
    </div>,
  ] as const;

  return <>{stepContent[step]}</>;
}

// ============================================
// BREADCRUMBS SHOWCASE
// ============================================

function BreadcrumbsShowcase() {
  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Breadcrumbs</h2>
      <p className={s.sectionDescription}>
        Navigation breadcrumb trail. Reads from route <code>staticData.breadcrumb</code> or accepts
        explicit <code>items</code> prop for standalone usage.
      </p>

      {/* --- Single item (current page) --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Single item (current page only)</h3>
        <Breadcrumbs items={[{ label: 'Dashboard' }]} />
      </div>

      {/* --- Two levels --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Two levels</h3>
        <Breadcrumbs
          items={[
            { label: 'Clients', path: '/w/dashboard' },
            { label: 'Acme Corp' },
          ]}
        />
      </div>

      {/* --- Three levels --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Three levels</h3>
        <Breadcrumbs
          items={[
            { label: 'Home', path: '/t' },
            { label: 'Profile', path: '/t/profile/self' },
            { label: 'Edit' },
          ]}
        />
      </div>

      {/* --- Admin navigation example --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Admin navigation example</h3>
        <Breadcrumbs
          items={[
            { label: 'Legal Entities', path: '/w/entities' },
            { label: 'Legal Entity Profile' },
          ]}
        />
      </div>

      {/* --- Route-derived (live) --- */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>Route-derived (live from current matches)</h3>
        <p style={{ fontSize: 13, color: '#6C726C', marginBottom: 8 }}>
          This instance reads from the current route match chain. Since the showcase route has no
          breadcrumb in its staticData, it renders nothing here.
        </p>
        <Breadcrumbs />
      </div>
    </section>
  );
}

// ============================================
// DATE / TIME PICKER SHOWCASE
// ============================================

const DATE_PICKER_SIZES: InputSize[] = ['sm', 'md', 'lg'];

function DateTimeShowcase() {
  const [dateValue, setDateValue] = useState('');
  const [timeValue12, setTimeValue12] = useState('');
  const [timeValue24, setTimeValue24] = useState('');
  const [dateTimeValue, setDateTimeValue] = useState('');

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Date / Time Pickers</h2>
      <p style={{ fontSize: 14, color: '#6C726C', marginBottom: 24 }}>
        Segment-based date and time inputs built on react-aria. String I/O (ISO format)
        for easy API/form integration.
      </p>

      {/* ============================== */}
      {/* DATE PICKER */}
      {/* ============================== */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>DatePicker</h3>

        {/* Default */}
        <div className={s.subsection}>
          <span className={s.label}>Default (no value)</span>
          <div style={{ maxWidth: 320 }}>
            <DatePicker />
          </div>
        </div>

        {/* With label + required */}
        <div className={s.subsection}>
          <span className={s.label}>Label + Required</span>
          <div style={{ maxWidth: 320 }}>
            <DatePicker label="Date of Birth" isRequired />
          </div>
        </div>

        {/* With value */}
        <div className={s.subsection}>
          <span className={s.label}>Controlled value</span>
          <div style={{ maxWidth: 320 }}>
            <DatePicker
              label="Start Date"
              value={dateValue || '2026-03-24'}
              onChange={(v) => setDateValue(v)}
            />
            <p style={{ fontSize: 12, color: '#6C726C', marginTop: 4 }}>
              Value: {dateValue || '2026-03-24'}
            </p>
          </div>
        </div>

        {/* With error */}
        <div className={s.subsection}>
          <span className={s.label}>Error state</span>
          <div style={{ maxWidth: 320 }}>
            <DatePicker
              label="Expiry Date"
              error="Date is required"
              isRequired
            />
          </div>
        </div>

        {/* With helper text */}
        <div className={s.subsection}>
          <span className={s.label}>Helper text</span>
          <div style={{ maxWidth: 320 }}>
            <DatePicker
              label="Hire Date"
              helperText="Select the employee's start date"
            />
          </div>
        </div>

        {/* With min/max */}
        <div className={s.subsection}>
          <span className={s.label}>Min/Max constraints</span>
          <div style={{ maxWidth: 320 }}>
            <DatePicker
              label="Booking Date"
              minDate="2026-03-01"
              maxDate="2026-03-31"
              helperText="March 2026 only"
            />
          </div>
        </div>

        {/* Disabled */}
        <div className={s.subsection}>
          <span className={s.label}>Disabled</span>
          <div style={{ maxWidth: 320 }}>
            <DatePicker label="Locked Date" disabled value="2026-01-15" />
          </div>
        </div>

        {/* Sizes */}
        <div className={s.subsection}>
          <span className={s.label}>Sizes</span>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {DATE_PICKER_SIZES.map((sz) => (
              <div key={sz} style={{ width: 240 }}>
                <DatePicker label={sz.toUpperCase()} size={sz} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ============================== */}
      {/* TIME PICKER */}
      {/* ============================== */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>TimePicker</h3>

        {/* Default */}
        <div className={s.subsection}>
          <span className={s.label}>Default</span>
          <div style={{ maxWidth: 240 }}>
            <TimePicker />
          </div>
        </div>

        {/* 12-hour */}
        <div className={s.subsection}>
          <span className={s.label}>12-hour format</span>
          <div style={{ maxWidth: 240 }}>
            <TimePicker
              label="Meeting Time"
              hourCycle={12}
              value={timeValue12 || undefined}
              onChange={setTimeValue12}
            />
            {timeValue12 && (
              <p style={{ fontSize: 12, color: '#6C726C', marginTop: 4 }}>
                Value: {timeValue12}
              </p>
            )}
          </div>
        </div>

        {/* 24-hour */}
        <div className={s.subsection}>
          <span className={s.label}>24-hour format</span>
          <div style={{ maxWidth: 240 }}>
            <TimePicker
              label="Shift Start"
              hourCycle={24}
              value={timeValue24 || undefined}
              onChange={setTimeValue24}
            />
            {timeValue24 && (
              <p style={{ fontSize: 12, color: '#6C726C', marginTop: 4 }}>
                Value: {timeValue24}
              </p>
            )}
          </div>
        </div>

        {/* With label */}
        <div className={s.subsection}>
          <span className={s.label}>Label + Required</span>
          <div style={{ maxWidth: 240 }}>
            <TimePicker label="Check-in Time" isRequired />
          </div>
        </div>

        {/* Error */}
        <div className={s.subsection}>
          <span className={s.label}>Error state</span>
          <div style={{ maxWidth: 240 }}>
            <TimePicker label="Deadline" error="Time is required" isRequired />
          </div>
        </div>

        {/* Disabled */}
        <div className={s.subsection}>
          <span className={s.label}>Disabled</span>
          <div style={{ maxWidth: 240 }}>
            <TimePicker label="Locked" disabled value="09:30" />
          </div>
        </div>

        {/* 15-min intervals */}
        <div className={s.subsection}>
          <span className={s.label}>15-min intervals</span>
          <div style={{ maxWidth: 240 }}>
            <TimePicker label="Appointment" hourCycle={12} timeIntervals={15} />
          </div>
        </div>

        {/* 60-min intervals */}
        <div className={s.subsection}>
          <span className={s.label}>60-min intervals</span>
          <div style={{ maxWidth: 240 }}>
            <TimePicker label="Hour slots" hourCycle={24} timeIntervals={60} />
          </div>
        </div>

        {/* Min/Max constrained */}
        <div className={s.subsection}>
          <span className={s.label}>Business hours (09:00 - 17:00)</span>
          <div style={{ maxWidth: 240 }}>
            <TimePicker
              label="Office hours"
              hourCycle={12}
              timeIntervals={30}
              minValue="09:00"
              maxValue="17:00"
            />
          </div>
        </div>

        {/* Sizes */}
        <div className={s.subsection}>
          <span className={s.label}>Sizes</span>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {DATE_PICKER_SIZES.map((sz) => (
              <div key={sz} style={{ width: 200 }}>
                <TimePicker label={sz.toUpperCase()} size={sz} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ============================== */}
      {/* DATE TIME PICKER */}
      {/* ============================== */}
      <div className={s.subsection}>
        <h3 className={s.subsectionTitle}>DateTimePicker</h3>

        {/* Default */}
        <div className={s.subsection}>
          <span className={s.label}>Default</span>
          <div style={{ maxWidth: 380 }}>
            <DateTimePicker />
          </div>
        </div>

        {/* Label + required */}
        <div className={s.subsection}>
          <span className={s.label}>Label + Required</span>
          <div style={{ maxWidth: 380 }}>
            <DateTimePicker label="Event Start" isRequired />
          </div>
        </div>

        {/* With value */}
        <div className={s.subsection}>
          <span className={s.label}>Controlled value</span>
          <div style={{ maxWidth: 380 }}>
            <DateTimePicker
              label="Scheduled At"
              value={dateTimeValue || '2026-03-24T14:30'}
              onChange={setDateTimeValue}
            />
            <p style={{ fontSize: 12, color: '#6C726C', marginTop: 4 }}>
              Value: {dateTimeValue || '2026-03-24T14:30'}
            </p>
          </div>
        </div>

        {/* Error */}
        <div className={s.subsection}>
          <span className={s.label}>Error state</span>
          <div style={{ maxWidth: 380 }}>
            <DateTimePicker
              label="Appointment"
              error="Date and time are required"
              isRequired
            />
          </div>
        </div>

        {/* Disabled */}
        <div className={s.subsection}>
          <span className={s.label}>Disabled</span>
          <div style={{ maxWidth: 380 }}>
            <DateTimePicker
              label="Locked"
              disabled
              value="2026-01-15T09:00"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// TABS SHOWCASE
// ============================================

const InfoCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1" />
    <path d="M8 7v4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="8" cy="5.25" r="0.75" fill="currentColor" />
  </svg>
);

function TabsShowcase() {
  const [defaultTab, setDefaultTab] = useState('expense');
  const [fullWidthTab, setFullWidthTab] = useState('overview');
  const [iconTab, setIconTab] = useState('expense');
  const [disabledTab, setDisabledTab] = useState('active');
  const [manyTab, setManyTab] = useState('tab1');
  const [countsTab, setCountsTab] = useState('ACTIVE');
  const [countsIconTab, setCountsIconTab] = useState('expense');

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Tabs</h2>

      {/* Default */}
      <div className={s.subsection}>
        <span className={s.label}>Default</span>
        <Tabs
          tabs={[
            { id: 'expense', label: 'Employee Expense' },
            { id: 'imprest', label: 'Imprest Expense' },
          ]}
          activeTab={defaultTab}
          onTabChange={setDefaultTab}
        />
        <p style={{ fontSize: 12, color: '#6C726C', marginTop: 8 }}>
          Active: {defaultTab}
        </p>
      </div>

      {/* Full Width */}
      <div className={s.subsection}>
        <span className={s.label}>Full Width</span>
        <Tabs
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'details', label: 'Details' },
            { id: 'history', label: 'History' },
          ]}
          activeTab={fullWidthTab}
          onTabChange={setFullWidthTab}
          fullWidth
        />
      </div>

      {/* With Icons */}
      <div className={s.subsection}>
        <span className={s.label}>With Icons</span>
        <Tabs
          tabs={[
            { id: 'expense', label: 'Employee Expense', icon: <InfoCircleIcon /> },
            { id: 'imprest', label: 'Imprest Expense', icon: <InfoCircleIcon /> },
          ]}
          activeTab={iconTab}
          onTabChange={setIconTab}
        />
      </div>

      {/* With Disabled Tabs */}
      <div className={s.subsection}>
        <span className={s.label}>With Disabled Tab</span>
        <Tabs
          tabs={[
            { id: 'active', label: 'Active' },
            { id: 'pending', label: 'Pending', disabled: true },
            { id: 'archived', label: 'Archived' },
          ]}
          activeTab={disabledTab}
          onTabChange={setDisabledTab}
        />
      </div>

      {/* With Counts */}
      <div className={s.subsection}>
        <span className={s.label}>With Counts</span>
        <Tabs
          tabs={[
            { id: 'ACTIVE', label: 'Active' },
            { id: 'ONBOARDING', label: 'Onboarding' },
            { id: 'ACTIVATION_PENDING', label: 'Activation Pending' },
            { id: 'INACTIVE', label: 'Inactive' },
          ]}
          activeTab={countsTab}
          onTabChange={setCountsTab}
          counts={{ ACTIVE: 24, ONBOARDING: 10, ACTIVATION_PENDING: 3, INACTIVE: 0 }}
        />
      </div>

      {/* With Counts + Icons */}
      <div className={s.subsection}>
        <span className={s.label}>With Counts + Icons</span>
        <Tabs
          tabs={[
            { id: 'expense', label: 'Employee Expense', icon: <InfoCircleIcon /> },
            { id: 'imprest', label: 'Imprest Expense', icon: <InfoCircleIcon /> },
          ]}
          activeTab={countsIconTab}
          onTabChange={setCountsIconTab}
          counts={{ expense: 142, imprest: 7 }}
        />
      </div>

      {/* With Counts + Disabled Tab */}
      <div className={s.subsection}>
        <span className={s.label}>With Counts + Disabled Tab</span>
        <Tabs
          tabs={[
            { id: 'active', label: 'Active' },
            { id: 'pending', label: 'Pending', disabled: true },
            { id: 'archived', label: 'Archived' },
          ]}
          activeTab="active"
          onTabChange={() => {}}
          counts={{ active: 15, pending: 2, archived: 0 }}
        />
      </div>

      {/* Many Tabs (scrollable) */}
      <div className={s.subsection}>
        <span className={s.label}>Many Tabs (scrollable)</span>
        <div style={{ maxWidth: 500 }}>
          <Tabs
            tabs={[
              { id: 'tab1', label: 'Tab One' },
              { id: 'tab2', label: 'Tab Two' },
              { id: 'tab3', label: 'Tab Three' },
              { id: 'tab4', label: 'Tab Four' },
              { id: 'tab5', label: 'Tab Five' },
              { id: 'tab6', label: 'Tab Six' },
              { id: 'tab7', label: 'Tab Seven' },
            ]}
            activeTab={manyTab}
            onTabChange={setManyTab}
          />
        </div>
      </div>
    </section>
  );
}

// ============================================
// LINE LOADER SHOWCASE
// ============================================

function LineLoaderShowcase() {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>LineLoader</h2>

      {/* Default (loading) */}
      <div className={s.subsection}>
        <span className={s.label}>Loading (default)</span>
        <div
          style={{
            border: '1px solid var(--wp-grey-200, #e8eae8)',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <LineLoader loading />
          <div style={{ padding: 16, textAlign: 'center', color: '#6c726c' }}>
            Table content area
          </div>
        </div>
      </div>

      {/* Not loading */}
      <div className={s.subsection}>
        <span className={s.label}>Not loading</span>
        <div
          style={{
            border: '1px solid var(--wp-grey-200, #e8eae8)',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <LineLoader loading={false} />
          <div style={{ padding: 16, textAlign: 'center', color: '#6c726c' }}>
            Table content area (loader hidden, no layout shift)
          </div>
        </div>
      </div>

      {/* Toggle demo */}
      <div className={s.subsection}>
        <span className={s.label}>Interactive toggle</span>
        <div className={s.row}>
          <Toggle
            isChecked={isLoading}
            onChange={setIsLoading}
            label="Toggle loading"
          />
        </div>
        <div
          style={{
            border: '1px solid var(--wp-grey-200, #e8eae8)',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <LineLoader loading={isLoading} />
          <div style={{ padding: 16, textAlign: 'center', color: '#6c726c' }}>
            Toggle the switch above to see the loader appear and disappear
          </div>
        </div>
      </div>
    </section>
  );
}
